import { useRef, useCallback, useEffect } from 'react';

type UseMutationObserverForEditorProps = {
  compId: string;
  isEditor: boolean;
  onStyleChange: () => void;
};

export const useMutationObserverForEditor = (
  params: UseMutationObserverForEditorProps,
) => {
  const { compId, isEditor, onStyleChange } = params;
  const mutationObserverRef = useRef<MutationObserver | undefined>();

  const getHostStyleElement = useCallback(() => {
    return new Promise<HTMLStyleElement | undefined>(resolve => {
      // `requestAnimationFrame` ensures style element is found
      requestAnimationFrame(() => {
        const compStyles = document.querySelector<HTMLStyleElement>(
          `style[data-comp-id="${compId}"]`,
        );

        return resolve(compStyles ?? undefined);
      });
    });
  }, [compId]);

  useEffect(() => {
    if (!isEditor) {
      return;
    }

    mutationObserverRef.current ??= new MutationObserver(onStyleChange);

    const mutationObserver = mutationObserverRef.current;

    getHostStyleElement().then(
      hostElementStyles => {
        if (hostElementStyles) {
          mutationObserver.observe(hostElementStyles, {
            characterData: true,
            subtree: true,
            childList: true,
          });
        }
      },
      () => {},
    );

    return () => {
      mutationObserver.disconnect();
    };
  }, [getHostStyleElement, onStyleChange, isEditor]);
};
